import { FC, useCallback, useEffect, useRef, useState } from "react";

/** Acknowledgement is per browser session, matching the reveal overlay: a reload should not
 * re-block someone who has already been told, but a fresh visit always is. */
const ACK_KEY = "sr:devEnvAcknowledged";

const readAck = (): boolean => {
  try {
    return sessionStorage.getItem(ACK_KEY) === "1";
  } catch {
    return false;
  }
};

/** The notice this build carries, or `null` on the competition build. */
export const useDevEnvironmentNotice = () => {
  const notice = window.__APP_CONFIG.devEnvironmentNotice ?? null;
  const [acknowledged, setAcknowledged] = useState(() => !notice || readAck());

  const acknowledge = useCallback(() => {
    try {
      sessionStorage.setItem(ACK_KEY, "1");
    } catch {
      /* sessionStorage unavailable — the strip still stands for the rest of the visit */
    }
    setAcknowledged(true);
  }, []);

  return { notice, acknowledged, acknowledge };
};

const WarningIcon: FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    className={className}
  >
    <path
      d="M10 6V10.5M10 13.5V14M3.5 17H16.5L10 3L3.5 17Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface DevEnvironmentGateProps {
  competitionUrl: string;
  onContinue: () => void;
}

/** The interstitial: covers the whole app until the visitor either leaves for the competition or
 * says they meant to be here. Deliberately not dismissible by Escape or a backdrop click — the
 * only ways out are the two explicit choices. */
export const DevEnvironmentGate: FC<DevEnvironmentGateProps> = ({
  competitionUrl,
  onContinue,
}) => {
  const leaveRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    leaveRef.current?.focus();
  }, []);

  // The app behind is inert while this stands, so keep the page from scrolling under it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sr-dev-env-title"
      className="fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto bg-sr-espresso px-4 py-10"
    >
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full border border-sr-tangerine/30 bg-sr-tangerine/10 text-sr-tangerine">
          <WarningIcon size={28} />
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-sr-tangerine">
            Development environment
          </p>
          <h1
            id="sr-dev-env-title"
            className="text-2xl font-medium text-sr-oat-milk sm:text-3xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            This is not the competition
          </h1>
          <p className="text-sm leading-relaxed text-sr-neutral-300">
            You have reached the Sugar Rush development environment. Deposits, trades and
            leaderboard positions here are test data, and count for nothing.
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-4">
          <a
            ref={leaveRef}
            href={competitionUrl}
            className="sr-focus-ring w-full rounded-xl bg-sr-lemon-muted px-5 py-3 text-sm font-medium text-sr-espresso transition-opacity hover:opacity-90"
          >
            Take me to the competition
          </a>
          <button
            type="button"
            onClick={onContinue}
            className="sr-focus-ring rounded-lg px-3 py-1.5 text-sm text-sr-neutral-500 transition-colors hover:text-sr-neutral-300"
          >
            Continue to the development environment
          </button>
        </div>
      </div>
    </div>
  );
};

/** The strip that stays for the rest of the session, so the warning survives the click-through. */
export const DevEnvironmentStrip: FC<{ competitionUrl: string }> = ({ competitionUrl }) => (
  <div className="border-b border-sr-tangerine/30 bg-sr-tangerine/10 px-4 py-2 sm:px-6">
    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
      <WarningIcon size={14} className="shrink-0 text-sr-tangerine" />
      <span className="text-xs font-medium text-sr-tangerine">
        Development environment — not the competition.
      </span>
      <a
        href={competitionUrl}
        className="sr-focus-ring rounded text-xs font-medium text-sr-oat-milk underline underline-offset-2 hover:text-white"
      >
        Go to the competition
      </a>
    </div>
  </div>
);
